import React from "react";
import { Instagram, Twitter, Facebook, MapPin, Phone, Mail } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_8129d42d-fed9-4788-882a-4169a0f332c3/artifacts/jb8ymdb6_image.png";

export default function Footer() {
  const { t } = useLanguage();
  const links = [
    { href: "#services", label: t("nav.services") },
    { href: "#reviews", label: t("nav.reviews") },
    { href: "#hours", label: t("nav.hours") },
    { href: "#location", label: t("nav.location") },
  ];

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#070707] border-t border-[rgba(212,175,55,0.18)]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Lopez Appliances" className="h-14 w-auto" />
              <div>
                <p className="font-serif-display text-xl text-white leading-none">
                  Lopez Appliances
                </p>
                <p className="text-[10px] tracking-[0.35em] uppercase text-[#d4af37] mt-1">
                  EST. 1985
                </p>
              </div>
            </div>
            <p className="mt-6 max-w-sm font-serif-display italic text-white/60 text-base">
              {t("footer.slogan")}
            </p>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-overline">{t("footer.quick_links")}</h4>
            <ul className="mt-5 space-y-3">
              {links.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    className="text-sm text-white/70 hover:text-[#d4af37] transition-colors"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="text-overline">{t("footer.contact")}</h4>
            <ul className="mt-5 space-y-3 text-sm text-white/70">
              <li className="flex gap-3">
                <MapPin size={16} className="text-[#d4af37] mt-0.5" />
                <span>{t("location.address")}</span>
              </li>
              <li>
                <a
                  href="tel:+14158268922"
                  className="flex gap-3 hover:text-[#d4af37] transition-colors"
                >
                  <Phone size={16} className="text-[#d4af37] mt-0.5" />
                  {t("location.phone")}
                </a>
              </li>
              <li>
                <a
                  href="mailto:lopezappliance85@gmail.com"
                  className="flex gap-3 hover:text-[#d4af37] transition-colors break-all"
                >
                  <Mail size={16} className="text-[#d4af37] mt-0.5 flex-shrink-0" />
                  {t("location.email")}
                </a>
              </li>
            </ul>

            <div className="mt-8">
              <h4 className="text-overline">{t("footer.follow")}</h4>
              <div className="flex items-center gap-4 mt-4">
                {[
                  { Icon: Instagram, href: "#", label: "Instagram" },
                  { Icon: Twitter, href: "#", label: "X" },
                  { Icon: Facebook, href: "#", label: "Facebook" },
                ].map(({ Icon, href, label }) => (
                  <a
                    key={label}
                    href={href}
                    aria-label={label}
                    data-testid={`footer-social-${label.toLowerCase()}`}
                    className="w-10 h-10 rounded-full border border-[rgba(212,175,55,0.35)] text-[#d4af37] flex items-center justify-center transition-all duration-300 hover:bg-[rgba(212,175,55,0.1)] hover:border-[#d4af37] hover:drop-shadow-[0_0_10px_rgba(212,175,55,0.8)] hover:-translate-y-0.5"
                  >
                    <Icon size={16} strokeWidth={1.6} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="gold-divider mt-16" />
        <p className="text-center text-xs tracking-[0.2em] uppercase text-white/40 mt-8">
          {t("footer.copyright")}
        </p>
      </div>
    </footer>
  );
}
